import clsx from 'clsx'
import React from 'react'
import type SvgIcon from '../SvgIcon/SvgIcon'
import type { UtilityColor } from '../utilities/colors'

export interface TagProps {
  /**
   * The color scheme of the tag
   * @default 'default'
   */
  color?: TagColor
  /**
   * The text content displayed in the tag
   */
  label: string
  /**
   * The size of the tag
   * @default 'medium'
   */
  size?: TagSize
  /**
   * Icon to display at the start of the tag
   */
  startAdornment?: typeof SvgIcon
}

type TagSize = 'large' | 'medium' | 'small'
type TagSizes = {
  [key in TagSize]: {
    root: string
    icon: string
    text: string
  }
}
type TagColor = UtilityColor | 'grey'
type TagColors = { [key in TagColor]: string }

const sizes: TagSizes = {
  large: {
    icon: 'w-5 mr-1',
    root: 'p-1',
    text: 'font-sans text-sm font-normal leading-4',
  },
  medium: {
    icon: 'w-4 mr-1',
    root: 'p-1',
    text: 'font-sans text-xs font-normal leading-3',
  },
  small: {
    icon: 'w-3 mr-0.5',
    root: 'p-0.75',
    text: 'font-sans text-xs font-normal leading-3',
  },
}

// TODO: Make shared values for these utility colors
export const colors: TagColors = {
  blue: 'border border-sky-300 bg-sky-100 text-sky-800',
  default: `border border-zinc-300 bg-zinc-100 text-zinc-800`,
  grey: `border border-gray-400 bg-gray-200 text-gray-800`,
  indigo: 'border border-violet-300 bg-violet-100 text-violet-700',
  pink: 'border border-pink-300 bg-pink-100 text-pink-700',
  purple: 'border border-fuchsia-300 bg-fuchsia-100 text-fuchsia-700',
  red: 'border border-rose-300 bg-rose-100 text-rose-700',
  teal: 'border border-teal-300 bg-teal-100 text-teal-800',
  yellow: 'border border-amber-300 bg-amber-100 text-amber-800',
}

function Tag({
  color = 'default',
  label,
  size = 'medium',
  startAdornment: StartAdornment,
}: TagProps): JSX.Element {
  return (
    <span
      className={clsx(
        'rounded-sm inline-flex cursor-default',
        colors[color],
        sizes[size].root,
      )}
    >
      <span className="flex items-center">
        {StartAdornment ? (
          <span className={clsx(sizes[size].icon)}>
            <StartAdornment />
          </span>
        ) : null}
        <span className={clsx('w-full', sizes[size].text)}>{label}</span>
      </span>
    </span>
  )
}
export default Tag
